// Profitability trend: sales, net profit and margin across periods of the
// selected window.
//
// A period whose settlements may still be arriving is drawn faded and labelled,
// so a short bar is not read as a collapse in trading. Where the synced history
// begins, the page says so rather than implying nothing was sold before it.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from 'recharts';
import { FiAlertTriangle } from 'react-icons/fi';

import local from '../ProfitMargin.module.css';
import { formatDate, money } from '../format';
import { getTrend } from '../services/profitApi';

const BUCKETS = [['DAY', 'Day'], ['WEEK', 'Week'], ['MONTH', 'Month']];

const daysIn = (window) => {
  if (!window?.start || !window?.end) return 0;
  return (new Date(window.end) - new Date(window.start)) / 86400000 + 1;
};

// The finest bucket that still gives a readable number of bars.
const defaultBucket = (window) => {
  const days = daysIn(window);
  if (days && days <= 31) return 'DAY';
  if (days && days <= 120) return 'WEEK';
  return 'MONTH';
};

const PointDetail = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <div className={local.card} style={{ padding: '8px 10px', margin: 0, fontSize: 12 }}>
      <strong>{formatDate(point.period_start)} – {formatDate(point.period_end)}</strong>
      {point.maturity === 'MATURING' && <div className={local.muted}>Still settling</div>}
      <div>Net sales {money(point.net_revenue)}</div>
      <div>Net profit {money(point.net_profit)}</div>
      <div>Margin {point.margin_pct === null ? '—' : `${point.margin_pct.toFixed(1)}%`}</div>
      {point.estimated && <div className={local.muted}>Includes estimated cost</div>}
      {point.excluded_revenue > 0 && (
        <div className={local.muted}>
          {money(point.excluded_revenue)} of sales with no cost left out
        </div>
      )}
    </div>
  );
};

const ProfitTrend = ({ token, applied, resolvedWindow }) => {
  const [bucket, setBucket] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const chosen = bucket || defaultBucket(resolvedWindow);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getTrend(token, applied, chosen)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) { setData(null); setError(err); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [token, applied, chosen]);

  const points = useMemo(() => data?.points ?? [], [data]);
  const anySettling = points.some((point) => point.maturity === 'MATURING');

  return (
    <div className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Profit over time</h2>
        <div className={local.toggle}>
          {BUCKETS.map(([key, label]) => (
            <button key={key} type="button"
              className={`${local.toggleBtn} ${chosen === key ? local.toggleBtnOn : ''}`}
              onClick={() => setBucket(key)}>{label}</button>
          ))}
        </div>
      </div>

      {error && (
        <div className={`${local.banner} ${local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>{error.message}</div>
        </div>
      )}

      {!error && (loading && !data ? <div className={local.empty}>Loading trend…</div>
        : points.length === 0 ? <div className={local.empty}>No settled trading in this period.</div>
          : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={points} margin={{ top: 8, right: 8, bottom: 4, left: 8 }}>
                <CartesianGrid stroke="#eef0f3" vertical={false} />
                <XAxis dataKey="period_start" tickFormatter={formatDate}
                  tick={{ fontSize: 11, fill: '#4b5563' }} minTickGap={24} />
                <YAxis yAxisId="money" tick={{ fontSize: 11, fill: '#4b5563' }} width={72}
                  tickFormatter={(value) => money(value)} />
                <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 11, fill: '#4b5563' }}
                  width={44} tickFormatter={(value) => `${value}%`} />
                <Tooltip content={<PointDetail />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar yAxisId="money" dataKey="net_revenue" name="Net sales" fill="#93c5fd">
                  {points.map((point) => (
                    <Cell key={point.period_start}
                      fillOpacity={point.maturity === 'MATURING' ? 0.45 : 1} />
                  ))}
                </Bar>
                <Bar yAxisId="money" dataKey="net_profit" name="Net profit" fill="#1d4ed8">
                  {points.map((point) => (
                    <Cell key={point.period_start}
                      fill={point.net_profit < 0 ? '#b91c1c' : '#1d4ed8'}
                      fillOpacity={point.maturity === 'MATURING' ? 0.45 : 1} />
                  ))}
                </Bar>
                <Line yAxisId="pct" dataKey="margin_pct" name="Margin %" stroke="#b45309"
                  strokeWidth={2} dot={{ r: 2 }} connectNulls={false} />
              </ComposedChart>
            </ResponsiveContainer>
          ))}

      <p className={local.note}>
        {anySettling && 'Faded bars are still settling and will rise as Amazon posts the rest. '}
        {data?.history_starts && (
          <>Settled history starts on {formatDate(data.history_starts)}; nothing earlier
            is shown, which is the limit of what has synced, not a period without trading. </>
        )}
        Advertising share appears once advertising is connected.
      </p>
    </div>
  );
};

export default ProfitTrend;
